import { Flex, Form } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { NetworkType, NETWORK_TEXT, ExpandNetworkType, RISK_LEVEL_ENUM, USER_CHECK_STATUS_ENUM } from "interfaces";
import { useStores } from "stores";
import { DefaultSelect, DefaultInput, CustomButton, EditableFile } from "ui";
import {
  _isRequired,
  renderSelectOptions,
  _isValidNetworkAddress,
  downloadFileBlob,
  useUserCheckWallet,
  getAmlRiskMessage,
} from "utils";
import { ENDPOINTS } from "services";
import { AmlAlert } from "components";
import { trimMask } from "utils";

export const NewAmlCheckModal = observer(() => {
  const { actionModals, user, checkLimit, amlCheckList } = useStores();
  const [form] = Form.useForm();

  const [selectedNetwork, setSelectedNetwork] = useState<NetworkType>(undefined);
  const [shouldCheckRisk, setShouldCheckRisk] = useState(false);
  const [walletToCheck, setWalletToCheck] = useState("");

  const [urlFile, setUrlFile] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const { isSeniorOperator, isJuniorOperator, isRiskManager } = user.roles;

  const isOperator = isSeniorOperator || isJuniorOperator || isRiskManager;

  const { riskLevel, checkId, status } = useUserCheckWallet(
    walletToCheck,
    shouldCheckRisk && !!walletToCheck && !!selectedNetwork
  );

  const createAmlCheck = async () => {
    const wallet = form.getFieldValue("address");

    if (wallet && selectedNetwork) {
      setWalletToCheck(wallet);
      setShouldCheckRisk(true);
    }
  };

  const handleNetworkChange = (value: ExpandNetworkType) => {
    const wallet = form.getFieldValue("address");

    setSelectedNetwork(value);

    if (wallet) {
      form.validateFields(["address"]);
    }
  };

  const handleCancelCreateCheck = () => {
    actionModals.closeAll();
    if (!isOperator) {
      checkLimit.refetchLimit();
    }
    amlCheckList.fetch();
  };

  const walletCheckProcess =
    !!status && [USER_CHECK_STATUS_ENUM.INITIAL, USER_CHECK_STATUS_ENUM.WAITING_AML_RESPONSE].includes(status);

  const hasResult = riskLevel && !walletCheckProcess && riskLevel !== RISK_LEVEL_ENUM.UNDEFINED;

  const showReport = hasResult && isOperator;

  useEffect(() => {
    if (checkId && showReport) {
      const getFile = async () => {
        try {
          const { filename, url } = await downloadFileBlob(ENDPOINTS.walletCheckReport(checkId));
          setFileName(filename);
          setUrlFile(url);
        } catch (e) {
          console.error(e);
        }
      };
      getFile();
    }
  }, [checkId, hasResult, riskLevel, showReport]);

  return (
    <Form form={form} name="createCheck" className="full-width" onFinish={() => createAmlCheck()}>
      <Flex vertical gap="large">
        <Flex vertical>
          <Form.Item
            name="network"
            rules={[_isRequired("Сеть обязательна для ввода")]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <DefaultSelect
              placeholder="Сеть"
              disabled={walletCheckProcess || hasResult}
              size="large"
              onChange={handleNetworkChange}
            >
              {renderSelectOptions(NETWORK_TEXT)}
            </DefaultSelect>
          </Form.Item>
          <Form.Item
            name="address"
            rules={[_isRequired("Введите кошелек"), _isValidNetworkAddress(selectedNetwork)]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <DefaultInput
              placeholder={`Адрес кошелька${selectedNetwork ? ` в сети ${NETWORK_TEXT[selectedNetwork]}` : ""}`}
              disabled={walletCheckProcess || hasResult}
              mask={trimMask}
            />
          </Form.Item>

          {hasResult && (
            <Flex vertical gap="middle">
              {hasResult && <AmlAlert amlMessage={getAmlRiskMessage(riskLevel)} />}
              {urlFile && showReport && (
                <EditableFile
                  file={{
                    url: urlFile,
                    docName: fileName,
                  }}
                />
              )}
            </Flex>
          )}
        </Flex>

        <Form.Item>
          <Flex gap="large" className="margin-top-lg">
            {!hasResult && (
              <CustomButton htmlType="submit" disabled={walletCheckProcess} loading={walletCheckProcess}>
                Проверить
              </CustomButton>
            )}
            <CustomButton
              htmlType="button"
              disabled={walletCheckProcess}
              type={hasResult ? "primary" : "default"}
              className={hasResult ? "full-width" : ""}
              onClick={handleCancelCreateCheck}
            >
              Закрыть
            </CustomButton>
          </Flex>
        </Form.Item>
      </Flex>
    </Form>
  );
});
